import { firebase, parseRef } from "../firebase"
import { isNumber } from 'lodash'

/**
 * This function is meant to be used as a callback to onSnapshop method from firebase.
 *
 * Example Use: .onSnapshot()
 *
 * @param commit
 * @param addName
 * @param removeName
 * @param snap
 */
export const defaultListener = (commit, addName, removeName, snap) => {
  const isMultiDoc = isNumber(snap.size)

  if (isMultiDoc) {
    snap.docChanges().forEach(change => {
      if (change.type === 'added') {
        const source = change.doc.metadata.hasPendingWrites ? 'Local' : 'Server'

        // Haven't fully decided how to handle this.
        // Since all entities are stored in a map, it doesnt matter if we receive
        // multiple update changes for the same document
        if (source === 'Server' || true) {
          const { id } = change.doc
          commit(addName, { id, ...change.doc.data() })
        }
      }

      if (change.type === 'removed') {
        commit(removeName, change.doc.id)
      }

      if (change.type === 'modified') {
        const { id } = change.doc
        commit(addName, { id, ...change.doc.data() })
      }
    })
  } else {
    if (snap.exists) {
      const doc = { id: snap.id, ...snap.data() }
      commit(addName, doc)
    } else {
      commit(removeName, snap.id)
    }
  }
}

export const generateId = (tenantId, collectionName) => {
  return firebase.firestore()
    .collection('tenants')
    .doc(tenantId)
    .collection(collectionName)
    .doc().id
}

export const generateStripeState = (options) => {
  const { state: givenState = {}, getters = {}, actions, mutationKey, api } = options

  const { collectionName } = givenState
  const { getPagination, getCollection } = getters

  const {
    get: getAction,
    list: listAction,
    update: updateAction,
    delete: deleteAction,
    create: createAction
  } = actions

  const {
    get: getApi,
    list: listApi,
    update: updateApi,
    delete: deleteApi,
    create: createApi
  } = api

  const mutations = {
    pagination: {
      limit: `${mutationKey}_LIMIT`,
      has_more: `${mutationKey}_HAS_MORE`,
      starting_after: `${mutationKey}_STARTING_AFTER`,
      ending_before: `${mutationKey}_ENDING_BEFORE`
    },
    update: `${mutationKey}_UPDATE`,
    set: `${mutationKey}_SET`
  }

  return {
    state: {
      [ collectionName ]: {},
      pagination: {
        has_more: false,
        limit: 50, // ..
        starting_after: undefined,
        ending_before: undefined,
      }
    },
    getters: {
      [ getPagination ](state) {
        return state.pagination
      },
      [ getCollection ](state) {
        return Object.values(state[ collectionName ])
      }
    },
    actions: {
      async [ getAction ]({ state, commit, dispatch }, data) {
        // You can supply a `_reload: true` param if you want to fetch from the API
        // instead of from the state (if it exists)
        const { _reload, id, expand, _headers = {} } = data || {}

        const payload = {
          id,
          headers: _headers
        }

        if (expand) {
          payload.params = {}
          payload.params.expand = expand
        }

        const fetchEntity = async () => await this.$api.get(getApi(data), payload)
          .then(res => {
            commit(mutations.update, res.data)
            return res.data
          })
          .catch(e => {
            dispatch('snackError', e)
          })

        const inCollection = id in state[ collectionName ]

        const entity = !_reload && inCollection
          ? state[ collectionName ][ id ]
          : await fetchEntity()

        return entity
      },
      [ listAction ]({ state, commit }, payload) {
        commit(mutations.set, [])
        const { type, query = {}, _headers: headers = {} } = payload
        const { limit, starting_after, ending_before, } = state.pagination

        const apiOptions = {
          params: { limit, ...query },
          headers,
        }

        if (type === 'next') {
          apiOptions.params.starting_after = starting_after
        } else if (type === 'prev') {
          apiOptions.params.ending_before = ending_before
        }

        const processResults = res => {
          const { has_more, data } = res.data

          commit(mutations.set, data)
          commit(mutations.pagination.has_more, !!has_more)

          if (has_more) {
            const lastElement = data.slice(-1).pop() || {}
            const firstElement = data[ 0 ] || {}
            const { id: lastId } = lastElement
            const { id: firstId } = firstElement

            commit(mutations.pagination.starting_after, lastId)
            commit(mutations.pagination.ending_before, firstId)
          } else if (type === 'next') {
            commit(state.pagination.starting_after, undefined)
          } else if (type === 'prev') {
            commit(state.pagination.ending_before, undefined)
          }

          return res.data
        }

        return this.$api.get(listApi(query), apiOptions)
          .then(processResults)
      },
      [ updateAction ]({ state, commit, dispatch }, data) {
        const { _headers: headers = {} } = data
        const payload = { ...data }
        const config = { headers }

        return this.$api.patch(updateApi(payload), payload, config)
          .then(res => {
            commit(mutations.update, res.data)
            return res.data
          })
          .catch(e => {
            dispatch('snackError', e)
            return e
          })
      },
      [ deleteAction ]({ state, commit, dispatch }, data) {
        const { _headers: headers = {} } = data
        const payload = { ...data }
        const config = {
          headers,
          data: payload
        }

        return this.$api.delete(deleteApi(payload), config)
          .then(res => {
            return res.data
          })
          .catch(e => {
            dispatch('snackError', e)
            return e
          })
      },
      [ createAction ]({ state, commit, dispatch }, data) {
        const { _headers: headers = {} } = data
        const payload = { ...data }
        const config = { headers }

        return this.$api.post(createApi(payload), payload, config)
          .then(res => {
            commit(mutations.update, res.data)
            return res.data
          })
          .catch(e => {
            dispatch('snackError', e)
            return e
          })
      }
    },
    mutations: {
      [ mutations.pagination.limit ](state, limit) {
        state.pagination.limit = limit
      },
      [ mutations.pagination.has_more ](state, hasMore) {
        state.pagination.has_more = hasMore
      },
      [ mutations.pagination.starting_after ](state, id) {
        state.pagination.starting_after = id
      },
      [ mutations.pagination.ending_before ](state, id) {
        state.pagination.ending_before = id
      },
      [ mutations.update ](state, payload) {
        const { id } = payload || {}

        if (id) {
          const stateClone = { ...state[ collectionName ] }
          stateClone[ id ] = payload
          state[ collectionName ] = stateClone
        }
      },
      [ mutations.set ](state, items) {
        state[ collectionName ] = items.reduce((acc, current) => {
          acc[ current.id ] = current

          return acc
        }, {})
      },
    }
  }
}
